import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState, PageSection, ProgressBar, StatusBadge } from "@/components/ds";
import { Info, Loader2 } from "@/components/ds/icons";
import { FuentesChips } from "@/components/capacitaciones-ia/FuentesChips";
import { capacitacionesIaApi } from "@/lib/api/capacitaciones-ia";
import type {
  CoberturaPersonaResponse,
  CoberturaPrograma,
  CoberturaProgramaSinDeclarar,
} from "@/types/capacitaciones-ia";

export interface CoberturaCapacitacionesProps {
  personaUuid: string;
}

function formatFecha(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Describe EL PROGRAMA, no la audiencia — mismo criterio que la fila
 * expandida del listado: "Dirigido a" se lee como una orden de envío y los
 * envíos no se deciden con este campo. Un ref colgante (cargo o área
 * eliminada) simplemente no aparece en `etiquetas`, no es un error a mostrar
 * acá. */
function etiquetaClasificacion(programa: CoberturaPrograma): string {
  const { tipo, etiquetas } = programa.publico_objetivo;
  if (tipo === "empresa") return "General — para cualquier trabajador";
  const nombres = etiquetas.join(", ") || "sin nombre disponible";
  return tipo === "cargo" ? `Por cargo: ${nombres}` : `Por área: ${nombres}`;
}

/** `fuentes[0]` (fuente primaria, `orden = 0`) cubre TANTO documento de
 * empresa como ad-hoc — `documento` (deprecado) queda como respaldo sólo si
 * `fuentes` llegara vacío. Antes de esto, un Programa ad-hoc-only sin
 * `nombre` propio caía directo en "Programa sin nombre" porque `documento`
 * es `null` para ese caso (sdd/nuevo-programa-multifuente, design #1663). */
function nombrePrograma(p: CoberturaPrograma | CoberturaProgramaSinDeclarar): string {
  return p.nombre ?? p.fuentes?.[0]?.etiqueta ?? p.documento?.label ?? "Programa sin nombre";
}

/**
 * Pestaña "Capacitaciones" de la Ficha del trabajador (SDD
 * cobertura-capacitaciones-por-trabajador, MR5 — design #1448, decisión 5).
 * Por cada Programa que le corresponde al trabajador (según público
 * objetivo), muestra cuántas temáticas ya enviadas cubrió y el desglose por
 * estado. Registra lo que pasó — no es un ranking ni un total de cumplimiento
 * cruzando programas (spec: "Cobertura por programa, no un total global").
 */
export function CoberturaCapacitaciones({ personaUuid }: CoberturaCapacitacionesProps) {
  const [data, setData] = useState<CoberturaPersonaResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setIsLoading(true);
    setError(null);
    capacitacionesIaApi
      .getCoberturaPersona(personaUuid)
      .then((res) => {
        if (!cancelado) setData(res);
      })
      .catch((e: unknown) => {
        if (!cancelado) {
          setError(e instanceof Error ? e.message : "No se pudo cargar la cobertura de capacitaciones.");
        }
      })
      .finally(() => {
        if (!cancelado) setIsLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [personaUuid]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          {error ?? "No se pudo cargar la cobertura de capacitaciones."}
        </CardContent>
      </Card>
    );
  }

  const { programas, programas_sin_publico_objetivo: sinDeclarar, resumen } = data;

  if (programas.length === 0 && sinDeclarar.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <EmptyState
            icono="school"
            mensaje="A este trabajador no le corresponde ningún programa con capacitaciones enviadas."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {programas.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Le corresponde{resumen.programas_aplicables === 1 ? "" : "n"}{" "}
          {resumen.programas_aplicables} programa{resumen.programas_aplicables === 1 ? "" : "s"} de
          capacitación con envíos ya realizados.
        </p>
      )}

      {programas.map((programa) => (
        <PageSection
          key={programa.id}
          titulo={
            <>
              {nombrePrograma(programa)}
              {programa.version > 1 && (
                <span className="text-meta font-normal"> · v{programa.version}</span>
              )}
            </>
          }
          descripcion={etiquetaClasificacion(programa)}
          accion={<ProgressBar valor={programa.cubiertas} total={programa.total} className="w-32" />}
        >
          {/* Fuentes del Programa (documento de empresa + ad-hoc,
              sdd/nuevo-programa-multifuente) — mismo chip que la fila
              expandida del listado, con descarga para las ad-hoc. Sin esto
              el trabajador podía ver una capacitación sin ningún rastro de
              qué documento la originó. */}
          {(programa.fuentes?.length ?? 0) > 0 && (
            <div className="border-border-subtle flex flex-wrap items-center gap-1.5 border-b px-4 py-2.5">
              <FuentesChips fuentes={programa.fuentes} />
            </div>
          )}
          <ul className="divide-border-subtle divide-y">
            {programa.tematicas.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {t.numero}. {t.titulo}
                  </p>
                  <p className="text-meta text-[12px]">Enviada el {formatFecha(t.enviado_at)}</p>
                </div>
                <StatusBadge estado={t.estado} />
              </li>
            ))}
          </ul>
          {programa.tematicas_no_enviadas > 0 && (
            <p className="text-meta border-border-subtle border-t px-4 py-2 text-[12px]">
              {programa.tematicas_no_enviadas} temática
              {programa.tematicas_no_enviadas === 1 ? "" : "s"} más programada
              {programa.tematicas_no_enviadas === 1 ? "" : "s"} a futuro — todavía no cuenta
              {programa.tematicas_no_enviadas === 1 ? "" : "n"} para esta cobertura.
            </p>
          )}
        </PageSection>
      ))}

      {sinDeclarar.length > 0 && (
        <PageSection titulo="Programas sin clasificar">
          <div className="flex items-start gap-2.5 px-4 py-3.5">
            <Info className="text-icon-soft mt-0.5 shrink-0 text-[18px]" />
            <div className="space-y-2 text-sm">
              <p className="text-muted-foreground">
                {/* El listado de Programas ya no tiene editor de
                    clasificación: se declara al crear el programa. Decir "se
                    clasifica desde Programa de Capacitaciones" mandaría a
                    buscar un botón que no existe. */}
                {sinDeclarar.length === 1
                  ? "No podemos calcular la cobertura de este programa porque nació sin indicar si es general o de ciertos cargos."
                  : "No podemos calcular la cobertura de estos programas porque nacieron sin indicar si son generales o de ciertos cargos."}{" "}
                Los programas nuevos lo declaran al crearse.
              </p>
              <ul className="text-foreground list-disc space-y-1 pl-4">
                {sinDeclarar.map((p) => (
                  <li key={p.id}>{nombrePrograma(p)}</li>
                ))}
              </ul>
            </div>
          </div>
        </PageSection>
      )}
    </div>
  );
}
